import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { FiX, FiChevronLeft, FiChevronRight } from "react-icons/fi";
import Button from "../../../../../../components/ui/Button";
import {
  MenuItem,
  Category,
  ItemVariant,
  getAllVariantsByItemId,
} from "../../../../../../utils/supabaseClient";
import { uploadToSupabaseStorage } from "../../../../../../utils/supabaseStorage";
import { SpiceLevel } from "../../../../../../types/restaurant";
import { ArTranslation } from "../../../../../../types/custom";
import { ItemIngredientsState } from "../../../../../../types/ingredient";
import BasicInformationTab from "./tabs/BasicInformationTab";
import AdditionalDetailsTab from "./tabs/AdditionalDetailsTab";
import ImageSettingsTab from "./tabs/ImageSettingsTab";
import LabelsTab from "./tabs/LabelsTab";
import DynamicOptions from "../add-item-modal/tabs/DynamicOptions";
import DietaryPreferencesTab from "./tabs/DietaryPreferencesTab";
import CrossSellingTab from "./tabs/CrossSellingTab";
import { updateMenuItemData } from "../../../../../../store/slices/menuItemSlice";
import { useAppDispatch } from "../../../../../../store/hooks";
import { ingredientService } from "../../../../../../services/ingredientService";

interface Option {
  name: string;
  maxedit: number;
  isMultipleSelection: boolean;
  isVariant: boolean;
  imageUrl?: string;
  children: { name: string; price: number; imageUrl?: string }[];
}

interface UpdateMenuItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onMenuItemUpdated: (menuItem: MenuItem) => void;
  menuItem: MenuItem;
  categories: Category[];
  branchId: string;
}

export interface FormData {
  name: string;
  description: string;
  price: string;
  discountedPrice: string;
  categoryId: string;
  isPublished: boolean;
  isAvailable: boolean;
  isFeatured: boolean;
  calories: string;
  preparationTime: string;
  spiceLevel: SpiceLevel;
  tags: number[];
}

export interface ArFormData {
  name: string;
  description: string;
}

const tabs = [
  { id: "basic", key: "basicInformation" },
  { id: "details", key: "additionalDetails" },
  { id: "options", key: "options" },
  // { id: "labels", key: "labels" },
  { id: "dietary", key: "dietaryPreferences" },
  { id: "crossSelling", key: "crossSelling" },
  { id: "image", key: "imageAndSettings" },
];

const UpdateMenuItemModal: React.FC<UpdateMenuItemModalProps> = ({
  isOpen,
  onClose,
  onMenuItemUpdated,
  menuItem,
  categories,
  branchId,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const dispatch = useAppDispatch();

  const [activeTab, setActiveTab] = useState("basic");
  const [formData, setFormData] = useState<FormData>({
    name: "",
    description: "",
    price: "",
    discountedPrice: "",
    categoryId: "",
    isPublished: true,
    isAvailable: true,
    isFeatured: false,
    calories: "",
    preparationTime: "",
    spiceLevel: "none" as SpiceLevel,
    tags: [],
  });

  const [arFormData, setArFormData] = useState<ArFormData>({
    name: "",
    description: "",
  });

  const [ingredients, setIngredients] = useState<string[]>([]);
  const [arIngredients, setArIngredients] = useState<string[]>([]);
  const [structuredIngredients, setStructuredIngredients] = useState<ItemIngredientsState>({
    individual_ingredients: [],
    ingredient_groups: [],
  });
  const [selectedFoodTags, setSelectedFoodTags] = useState<number[]>([]);
  const [selectedDietaryPreferences, setSelectedDietaryPreferences] = useState<string[]>([]);
  const [selectedOptions, setSelectedOptions] = useState<ItemVariant[]>([]);
  const [selectedCrossSellItems, setSelectedCrossSellItems] = useState<string[]>([]);
  const [originalCrossSellItems, setOriginalCrossSellItems] = useState<string[]>([]);
  
  // Original state for delta calculation
  const [originalIngredients, setOriginalIngredients] = useState<string[]>([]);
  const [originalGroups, setOriginalGroups] = useState<string[]>([]);
  const [originalDietaries, setOriginalDietaries] = useState<string[]>([]);
  const [originalOptions, setOriginalOptions] = useState<string[]>([]);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageChanged, setImageChanged] = useState(false);
  const [imageRemoved, setImageRemoved] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form data when menuItem changes
  useEffect(() => {
    if (menuItem) {
      setFormData({
        name: menuItem.name || "",
        description: menuItem.description || "",
        price: menuItem.price?.toString() || "",
        discountedPrice: menuItem.discounted_price?.toString() || "0",
        categoryId: menuItem.category_id || "",
        isPublished: menuItem.is_published ?? true,
        isAvailable: menuItem.is_available ?? true,
        isFeatured: menuItem.is_featured ?? false,
        calories: menuItem.calories?.toString() || "",
        preparationTime: menuItem.preparation_time?.toString() || "",
        spiceLevel: (menuItem.spice_level === "extra_hot" ? "extraHot" : menuItem.spice_level) || "none",
        tags: menuItem.tags || []
      });

      setArFormData({
        name: (menuItem.ar as ArTranslation)?.name || "",
        description: (menuItem.ar as ArTranslation)?.description || "",
      });

      setIngredients(((menuItem as any).ingredients as string[]) || []);
      setArIngredients(((menuItem as any).ar as ArTranslation)?.ingredients || []);
      setSelectedFoodTags(menuItem.tags || []);
      setSelectedOptions((menuItem as any).options || []);
      setImagePreview(menuItem.image_url || null);
      setImageChanged(false);
      setImageRemoved(false);
      setError(null);

      const loadStructuredIngredients = async (itemId: string) => {
        try {
          const data = await ingredientService.getItemIngredients(itemId);
          const individual = data
            .filter((entry) => entry.ingredient_id && !entry.group_id)
            .map((entry) => entry.ingredient_id as string);
          const groups = data
            .filter((entry) => entry.group_id && !entry.ingredient_id)
            .map((entry) => entry.group_id as string);

          setStructuredIngredients({
            individual_ingredients: Array.from(new Set(individual)),
            ingredient_groups: Array.from(new Set(groups)),
          });
          // Save original state for delta calculation
          setOriginalIngredients(Array.from(new Set(individual)));
          setOriginalGroups(Array.from(new Set(groups)));
        } catch (err) {
          console.error("Failed to load structured ingredients", err);
          setStructuredIngredients({ individual_ingredients: [], ingredient_groups: [] });
        }
      };

      if (menuItem.id) {
        loadStructuredIngredients(menuItem.id);
        
        // Load existing variants for this item
        const loadVariants = async () => {
          try {
            const variantsResult = await getAllVariantsByItemId(menuItem.id);
            if (variantsResult.data && variantsResult.data.length > 0) {
              const transformedVariants: ItemVariant[] = variantsResult.data.map((variant: any) => ({
                id: variant.id,
                data: variant.data,
                item_id: variant.item_id,
                form_name: variant.form_name,
                action_type: variant.action_type,
                branch_action_config_id: variant.branch_action_config_id,
                override_data: variant.override_data || null,
              }));
              setSelectedOptions(transformedVariants);
              // Save original state for delta calculation
              setOriginalOptions(transformedVariants.map(v => v.branch_action_config_id));
            } else {
              setOriginalOptions([]);
            }
          } catch (err) {
            console.error("Failed to load variants", err);
          }
        };
        loadVariants();
      } else {
        setStructuredIngredients({ individual_ingredients: [], ingredient_groups: [] });
      }
    }
  }, [menuItem]);

  const handleImageChange = (file: File | null) => {
    setImageChanged(true);
    setImageRemoved(!file);
    setImageFile(file);
  };

  const validateForm = () => {
    const newErrors: string[] = [];

    if (!formData.name.trim()) {
      newErrors.push(t("menuItems.errors.nameRequired"));
    }

    if (
      !formData.price ||
      isNaN(Number(formData.price)) ||
      Number(formData.price) <= 0
    ) {
      newErrors.push(t("menuItems.errors.invalidPrice"));
    }

    if (
      formData.discountedPrice &&
      formData.discountedPrice.trim() !== "" &&
      (isNaN(Number(formData.discountedPrice)) ||
        Number(formData.discountedPrice) < 0)
    ) {
      newErrors.push(t("menuItems.errors.invalidDiscountedPrice"));
    }

    if (
      formData.discountedPrice &&
      formData.discountedPrice.trim() !== "" &&
      Number(formData.discountedPrice) >= Number(formData.price)
    ) {
      newErrors.push(t("menuItems.errors.discountedPriceTooHigh"));
    }

    if (!formData.categoryId) {
      newErrors.push(t("menuItems.errors.categoryRequired"));
    }

    if (
      formData.calories &&
      formData.calories.trim() !== "" &&
      (isNaN(Number(formData.calories)) || Number(formData.calories) < 0)
    ) {
      newErrors.push(t("menuItems.errors.invalidCalories"));
    }

    if (
      formData.preparationTime &&
      formData.preparationTime.trim() !== "" &&
      (isNaN(Number(formData.preparationTime)) ||
        Number(formData.preparationTime) < 0)
    ) {
      newErrors.push(t("menuItems.errors.invalidPreparationTime"));
    }

    // Note: Variant validation is now handled by the DynamicOptions component

    return newErrors;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      setError(validationErrors[0]);
      return;
    }

    setIsLoading(true);

    try {
      // Handle image upload if changed
      let imageUrl = menuItem.image_url;

      if (imageChanged) {
        if (imageRemoved) {
          imageUrl = "";
        } else if (imageFile) {
          const uniqueFileName = `${Date.now()}-${Math.random()
            .toString(36)
            .substr(2, 9)}-${imageFile.name}`;
          const uploadResult = await uploadToSupabaseStorage(
            imageFile,
            uniqueFileName,
            {
              bucket: "uploads",
              path: `menu-items/${branchId}`,
            }
          );
          imageUrl = uploadResult.publicUrl;
        }
      }

      // Compute deltas for all relational data
      const ingredientsAdd = structuredIngredients.individual_ingredients.filter(
        id => !originalIngredients.includes(id)
      );
      const ingredientsRemove = originalIngredients.filter(
        id => !structuredIngredients.individual_ingredients.includes(id)
      );
      const groupsAdd = structuredIngredients.ingredient_groups.filter(
        id => !originalGroups.includes(id)
      );
      const groupsRemove = originalGroups.filter(
        id => !structuredIngredients.ingredient_groups.includes(id)
      );
      const dietariesAdd = selectedDietaryPreferences.filter(
        id => !originalDietaries.includes(id)
      );
      const dietariesRemove = originalDietaries.filter(
        id => !selectedDietaryPreferences.includes(id)
      );
      
      // Options delta (parallel arrays for add)
      const currentOptionIds = selectedOptions.map(o => o.branch_action_config_id);
      const optionsAdd = selectedOptions
        .filter(o => !originalOptions.includes(o.branch_action_config_id))
        .map(o => o.branch_action_config_id);
      const optionsDataAdd = selectedOptions
        .filter(o => !originalOptions.includes(o.branch_action_config_id))
        .map(o => o.override_data || null);
      const optionsRemove = originalOptions.filter(
        id => !currentOptionIds.includes(id)
      );
      
      // Cross-selling delta
      const crossSellingAdd = selectedCrossSellItems.filter(
        id => !originalCrossSellItems.includes(id)
      );
      const crossSellingRemove = originalCrossSellItems.filter(
        id => !selectedCrossSellItems.includes(id)
      );

      const dataToUpdate = {
        id: menuItem.id,
        
        // --- 1. Main Item Updates (sent normally) ---
        name: formData.name,
        description: formData.description,
        price: Number(formData.price),
        category_id: formData.categoryId,
        is_published: formData.isPublished,
        is_available: formData.isAvailable,
        is_featured: formData.isFeatured,
        discounted_price: formData.discountedPrice && formData.discountedPrice.trim() !== ""
          ? Number(formData.discountedPrice)
          : 0,
        image_url: imageUrl,
        spice_level: formData.spiceLevel === "extraHot" ? "extra_hot" : formData.spiceLevel,
        calories: formData.calories && formData.calories.trim() !== ""
          ? Number(formData.calories)
          : null,
        preparation_time: formData.preparationTime && formData.preparationTime.trim() !== ""
          ? Number(formData.preparationTime)
          : null,
        updated_at: new Date(Date.now()).toISOString(),
        ar: {
          name: arFormData.name || null,
          description: arFormData.description || null,
          ingredients: arIngredients.length > 0 ? arIngredients : null,
        },
        tags: selectedFoodTags,
        
        // --- 2. Ingredients & Groups (delta only) ---
        ...(ingredientsAdd.length > 0 && { ingredients_add: ingredientsAdd }),
        ...(ingredientsRemove.length > 0 && { ingredients_remove: ingredientsRemove }),
        ...(groupsAdd.length > 0 && { groups_add: groupsAdd }),
        ...(groupsRemove.length > 0 && { groups_remove: groupsRemove }),
        
        // --- 3. Dietaries (delta only) ---
        ...(dietariesAdd.length > 0 && { dietaries_add: dietariesAdd }),
        ...(dietariesRemove.length > 0 && { dietaries_remove: dietariesRemove }),
        
        // --- 4. Options (delta with parallel arrays) ---
        ...(optionsAdd.length > 0 && { options_add: optionsAdd, options_data_add: optionsDataAdd }),
        ...(optionsRemove.length > 0 && { options_remove: optionsRemove }),
        
        // --- 5. Cross-Selling (delta) ---
        ...(crossSellingAdd.length > 0 && { cross_selling_add: crossSellingAdd }),
        ...(crossSellingRemove.length > 0 && { cross_selling_remove: crossSellingRemove }),
      };

      const updatedItem = await dispatch(updateMenuItemData(dataToUpdate)).unwrap();

      // Notify parent (MenuItemsPage uses server-side pagination with local state)
      onMenuItemUpdated(updatedItem);

      handleClose();
    } catch (err) {
      console.error("Error updating menu item:", err);
      setError(t("menuItems.errors.updateFailed"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setImageFile(null);
    setImageChanged(false);
    setImageRemoved(false);
    setError(null);
    setActiveTab("basic");
    setStructuredIngredients({ individual_ingredients: [], ingredient_groups: [] });
    setSelectedCrossSellItems([]);
    setOriginalCrossSellItems([]);
    // Reset all original states for delta calculation
    setOriginalIngredients([]);
    setOriginalGroups([]);
    setOriginalDietaries([]);
    setOriginalOptions([]);
    setSelectedDietaryPreferences([]);
    setSelectedOptions([]);
    onClose();
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case "basic":
        return (
          <BasicInformationTab
            formData={formData}
            arFormData={arFormData}
            categories={categories}
            onFormDataChange={setFormData}
            onArFormDataChange={setArFormData}
          />
        );
      case "details":
        return (
          <AdditionalDetailsTab
            formData={formData}
            ingredients={ingredients}
            arIngredients={arIngredients}
            onFormDataChange={setFormData}
            onIngredientsChange={setIngredients}
            onArIngredientsChange={setArIngredients}
            branchId={branchId}
            itemId={menuItem.id}
            itemIngredients={structuredIngredients}
            onItemIngredientsChange={setStructuredIngredients}
          />
        );
      case "options":
        console.log('Update Modal - Branch ID:', branchId, 'Menu Item ID:', menuItem?.id);
        return (
          <DynamicOptions
            itemId={menuItem?.id}
            branchId={branchId}
            selectedVariants={selectedOptions}
            onVariantsChange={setSelectedOptions}
          />
        );
      case "labels":
        return (
          <LabelsTab
            itemFormData={{
              ...formData,
              ingredients,
              id: menuItem.id,
              category: categories.find(
                (cat) => cat.id === formData.categoryId
              ),
            }}
            selectedFoodTags={selectedFoodTags}
            onFoodTagsChange={setSelectedFoodTags}
          />
        );
      case "dietary":
        return (
          <DietaryPreferencesTab
            itemId={menuItem.id || ""}
            selectedDietaryPreferences={selectedDietaryPreferences}
            onDietaryPreferencesChange={setSelectedDietaryPreferences}
            onOriginalDietariesLoaded={setOriginalDietaries}
          />
        );
      case "crossSelling":
        return (
          <CrossSellingTab
            branchId={branchId}
            triggerItemId={menuItem.id || ""}
            selectedSuggestions={selectedCrossSellItems}
            onSuggestionsChange={setSelectedCrossSellItems}
            onOriginalSuggestionsLoaded={setOriginalCrossSellItems}
          />
        );
      case "image":
        return (
          <ImageSettingsTab
            formData={formData}
            imageFile={imageFile}
            imagePreview={imagePreview}
            onFormDataChange={setFormData}
            onImageChange={handleImageChange}
            onImagePreviewChange={setImagePreview}
          />
        );
      default:
        return null;
    }
  };

  const modalVariants = {
    hidden: { opacity: 0, scale: 0.95 },
    visible: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 0.95 },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-0 sm:p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/25 backdrop-blur-sm"
              onClick={handleClose}
            />

            <motion.div
              variants={modalVariants}
              initial="hidden"
              animate="visible"
              exit="exit"
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="relative bg-white dark:bg-gray-800 rounded-none sm:rounded-xl shadow-2xl w-full h-screen sm:h-auto sm:min-h-[90vh] sm:max-h-[90vh] sm:max-w-6xl mx-auto flex flex-col overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {t("menuItems.editItem")}
                </h2>
                <button
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors cursor-pointer"
                >
                  <FiX size={24} />
                </button>
              </div>

              {/* Tabs Navigation */}
              <div className="border-b border-gray-200 dark:border-gray-700 px-6">
                <div className="flex overflow-x-auto -mb-px">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`px-4 py-3 text-nowrap text-sm font-medium border-b-2 transition-colors cursor-pointer flex-shrink-0 ${activeTab === tab.id
                        ? "border-primary text-primary"
                        : "border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300"
                        }`}
                    >
                      {t(`menuItems.${tab.key}`)}
                    </button>
                  ))}
                </div>
              </div>

              <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
                {/* Tab Content */}
                <div className="p-6 flex-1 overflow-y-auto">
                  {error && (
                    <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                      <p className="text-sm text-red-600 dark:text-red-400">
                        {error}
                      </p>
                    </div>
                  )}

                  {renderActiveTab()}
                </div>

                {/* Actions */}
                <div className="flex gap-3 px-6 py-4 border-t border-gray-200 dark:border-gray-700">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClose}
                    disabled={isLoading}
                    className="flex-1"
                  >
                    {t("common.cancel")}
                  </Button>
                  <Button
                    type="submit"
                    isLoading={isLoading}
                    disabled={
                      !formData.name.trim() ||
                      !formData.price ||
                      !formData.categoryId
                    }
                    className="flex-1"
                    rightIcon={isRTL ? <FiChevronLeft /> : <FiChevronRight />}
                  >
                    {t("menuItems.updateItem")}
                  </Button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default UpdateMenuItemModal;
